// Firebase API Service for Phoenix Rising
const FIREBASE_FUNCTIONS_URL = process.env.NODE_ENV === 'development' 
  ? 'http://127.0.0.1:5001/phoenix-rising-creator360/us-central1'
  : 'https://us-central1-phoenix-rising-creator360.cloudfunctions.net';

export interface ContentGenerationRequest {
  prompt: string;
  platform: string;
  contentType: string;
  userId?: string;
}

export interface ContentGenerationResponse {
  success: boolean;
  content: string;
  contentId: string;
}

export interface AnalyticsRequest {
  userId: string;
  event: string;
  data: Record<string, unknown>;
}

export interface PaymentRequest {
  userId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
}

export interface DashboardData {
  contentCount: number;
  paymentsCount: number;
  recentAnalytics: Record<string, unknown>[];
}

class FirebaseApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = FIREBASE_FUNCTIONS_URL;
  }

  // Generate content using AI
  async generateContent(request: ContentGenerationRequest): Promise<ContentGenerationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/generateContent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating content:', error);
      throw error;
    }
  }

  // Track analytics
  async trackAnalytics(request: AnalyticsRequest): Promise<{ success: boolean }> {
    try {
      const response = await fetch(`${this.baseUrl}/trackAnalytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error tracking analytics:', error);
      throw error;
    }
  }

  // Process payment
  async processPayment(request: PaymentRequest): Promise<{ success: boolean; paymentId: string; status: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/processPayment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  // Get dashboard data
  async getDashboardData(userId: string): Promise<DashboardData> {
    try {
      const response = await fetch(`${this.baseUrl}/getDashboardData?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; service: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/healthCheck`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  }
}

export const firebaseApi = new FirebaseApiService();

// Ollama Integration
export const generateContentWithOllama = async (prompt: string, model: string = 'llama3.2') => {
  try {
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/generateContentWithOllama`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        model,
        userId: 'user123' // Replace with actual user ID
      })
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating content with Ollama:', error);
    throw error;
  }
};

export const getOllamaModels = async () => {
  try {
    const response = await fetch(`${FIREBASE_FUNCTIONS_URL}/getOllamaModels`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting Ollama models:', error);
    throw error;
  }
}; 